import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Button, makeStyles, Snackbar, TextField } from '@material-ui/core'

import config from '../../../config'
import actions from '../../../redux/actions'
import Loader from '../../../common/components/notifications/Loader'
import GroupsTable from './GroupsTable'
import { ADD_CONTENT_GROUP_CATEGORIES, ADD_CONTENT_GROUP_USER, CREATE_CONTENT_GROUP, DELETE_CONTENT_GROUP, GET_CATEGORIES, GET_USERS, REMOVE_CONTENT_GROUP_CATEGORIES, REMOVE_CONTENT_GROUP_USER, UPDATE_CONTENT_GROUP_META } from '../../../redux/data/types'
import queryString from '../../../utils/QueryString'
import GroupDetails from './GroupDetails'
import { getHasRequestError, getHasRequestPending } from '../../../redux/data/selectors'
import { Alert } from '@material-ui/lab'
import { cloneDeep, uniqBy } from 'lodash'
import RestrictedComponent from '../../../lib/components/form/RestrictedComponent'
import schema from '../../../routes/permissions/schema'

const useStyles = makeStyles(theme => ({
  separator: {
    margin: '5px 0'
  },
  createButton: {
    marginLeft: '1rem',
    verticalAlign: 'text-top'
  }
}))

const getDefaultContentGroup = (clientID) => ({
  Group: {
    groupName: '',
    groupDescription: '',
    clientID
  },
  GroupUsers: [],
  GroupCategories: []
})

const GroupManager = props => {
  const {
    categoriesLoaded,
    categoriesPending,
    requestCategories,
    clientID,
    saveContentGroup,
    requestPending,
    requestError,
    assignUsersToGroup,
    removeUserFromGroup,
    usersLoaded,
    usersPending,
    requestUsers
  } = props
  //@FIXME: this could be replaced with some routing based on the querystring "contentGroup"
  // but this component isn't rerendering when the location.search is updated 
  const [selectedContentGroup, setSelectedContentGroup] = useState(queryString.parse(location.search).contentGroup)
  const [creatingNew, setCreatingNew] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(!!requestPending || !!requestError)
  const [creatingGroup, setCreatingGroup] = useState(getDefaultContentGroup(clientID))
  const [filter, setFilter] = useState('')
  const classes = useStyles()

  let snackMessage = requestPending ? 'In Progress' : 'Success'
  if (requestError) {
    snackMessage = 'Error: ' + requestError
  }

  const handleCloseSnackbar = () => setShowSnackbar(false)

  const Snack = <Snackbar open={showSnackbar} autoHideDuration={9000} onClose={handleCloseSnackbar}>
    <Alert onClose={handleCloseSnackbar} severity={requestError ? 'error' : requestPending ? 'info' : 'success'}>
      {snackMessage}
    </Alert>
  </Snackbar>

  useEffect(() => {
    if (requestPending || requestError) {
      setShowSnackbar(true)
    }
  }, [requestPending, requestError])

  useEffect(() => {
    if (!categoriesPending && !categoriesLoaded) {
      requestCategories()
    }
  }, [categoriesPending, categoriesLoaded])

  const handleCreateNew = () => {
    setCreatingNew(true)
  }

  useEffect(() => {
    if (!usersPending && !usersLoaded) {
      requestUsers()
    }
  }, [usersPending, usersLoaded])

  if (!categoriesLoaded || usersPending || !usersLoaded) {
    return <Loader />
  }

  const handleAssignUsers = (users, groupId) => {
    assignUsersToGroup(users, groupId)
  }

  const handleRemoveUser = (user, groupId) => {
    removeUserFromGroup(user, groupId)
  }

  if (selectedContentGroup) {
    return <>
      <GroupDetails
        categoriesPending={categoriesPending}
        categoriesLoaded={categoriesLoaded}
        requestCategories={requestCategories}
        onSave={saveContentGroup}
        id={selectedContentGroup}
        onClose={() => setSelectedContentGroup(false)}
        assignUsers={users => handleAssignUsers(users, selectedContentGroup)}
        removeUser={user => handleRemoveUser(user, selectedContentGroup)}
      />
      {Snack}
    </>
  }
  if (creatingNew) {
    return <>
      <GroupDetails
        categoriesPending={categoriesPending}
        categoriesLoaded={categoriesLoaded}
        requestCategories={requestCategories}
        onSave={saveContentGroup}
        contentGroup={creatingGroup}
        onClose={() => setCreatingNew(false)}
        assignUsers={users => {
          // add the users to the content group being created
          // when the group is saved (created) add them after
          const updatedContentGroup = cloneDeep(creatingGroup)
          updatedContentGroup.GroupUsers = uniqBy([...updatedContentGroup.GroupUsers, ...users], user => user.userId)
          setCreatingGroup(updatedContentGroup)
        }}
        removeUser={user => {
          const newGroupUsers = [...creatingGroup.GroupUsers].filter(groupUser => groupUser.userId !== user.userId)
          setCreatingGroup({ ...creatingGroup, GroupUsers: newGroupUsers })
        }}
      />
      {Snack}
    </>
  }

  return <div className="panel">
    <div className="panel-heading">
      <div>
        <h4>Content Groups</h4>
        <div>
          <TextField
            size="small"
            placeholder="Search"
            // label="Search"
            variant="outlined"
            value={filter}
            onChange={ev => setFilter(ev.target.value)}
          />
          <RestrictedComponent permissionCheck={schema.ACCOUNT_CONTENT_GROUPS.CREATE}>
            <Button className={classes.createButton} variant="contained" color="primary" onClick={handleCreateNew}>
              Create New
          </Button>
          </RestrictedComponent>
        </div>
      </div>
    </div>
    <hr className={classes.separator} />
    <div className="panel-body">
      <GroupsTable selectGroup={id => setSelectedContentGroup(id)} searchFilter={filter} />
    </div>
    {Snack}
  </div>
}

const getDifference = (newObject, oldObject, equalityFunction) => {
  // those that exist in the new but not in the old
  const addedItems = newObject.filter(cat => !oldObject.find(oldCat => equalityFunction(cat, oldCat)))
  // those that exist in the old but not in the new
  const removedItems = oldObject.filter(oldCat => !newObject.find(cat => equalityFunction(cat, oldCat)))
  return { addedItems, removedItems }
}

const mapStateToProps = state => {
  const categoriesPending = state.request?.[GET_CATEGORIES]?.pending
  const categoriesLoaded = (state.request[GET_CATEGORIES] &&
    state.request[GET_CATEGORIES]?.pending === false) ||
    (state.request[GET_CATEGORIES] && state.request[GET_CATEGORIES]?.pending === false)
  const clientID = state.data.user.clientId

  const requestTypes = [
    UPDATE_CONTENT_GROUP_META,
    CREATE_CONTENT_GROUP,
    ADD_CONTENT_GROUP_CATEGORIES,
    REMOVE_CONTENT_GROUP_CATEGORIES,
    DELETE_CONTENT_GROUP
  ]

  const requestPending = requestTypes.some(type => getHasRequestPending(state, type))
  const requestError = requestTypes.some(type => getHasRequestError(state, type))

  const usersRequests = [GET_USERS]

  const usersLoaded = usersRequests.some(type => state.request[type] && state.request[type].pending === false)
  const usersPending = usersRequests.some(type => getHasRequestPending(state, type))

  return { categoriesPending, categoriesLoaded, clientID, requestPending, requestError, usersLoaded, usersPending }
}

const mapDispatchToProps = dispatch => ({
  callDispatch: func => dispatch(func),
})

const mergeProps = (stateProps, _dispatchProps, ownProps) => {
  const dispatch = func => _dispatchProps.callDispatch(func)
  const requestCategories = () => {
    dispatch(actions.request.requestCategories())
  }
  const saveContentGroup = (updatedContentGroup, originalContentGroup, addUsers) => {
    // content updates are broken into several different api calls
    // newGroup
    // updateGroupMeta: name, description
    // addGroupCategories, removeGroupCategory, getGroupCategories
    const hasMetaChange = updatedContentGroup.Group.groupName !== originalContentGroup.Group.groupName ||
      updatedContentGroup.Group.groupDescription !== originalContentGroup.Group.groupDescription

    const { addedItems: addedCategories, removedItems: removedCategories } = getDifference(updatedContentGroup.GroupCategories, originalContentGroup.GroupCategories, (categoryA, categoryB) => categoryA.categoryID === categoryB.categoryID)
    if (!updatedContentGroup.Group.groupID) {
      // group being created
      const postData = {
        clientID: stateProps.clientID,
        groupName: updatedContentGroup.Group.groupName,
        groupDescription: updatedContentGroup.Group.groupDescription
      }
      dispatch(actions.request.sendRequest({
        type: CREATE_CONTENT_GROUP,
        url: `${config.uris.crud}Clientcontentgroups/newGroup`,
        postData,
        withResult: (err, res) => {
          // do the save again to make the additional requests
          // find the group just created in the response
          let responseGroup
          for (let i = res.data.data.length - 1; i >= 0; i--) {
            const group = res.data.data[i]
            if (
              group.Group.groupName === updatedContentGroup.Group.groupName &&
              group.Group.groupDescription === updatedContentGroup.Group.groupDescription &&
              group.GroupUsers.length === 0 &&
              group.GroupCategories.length === 0
            ) {
              // assume this is the one that was just created
              responseGroup = group
              break
            }
          }
          if (!responseGroup) {
            // no results matched the request made 
            throw "Request Error: The response did not include the new content group"
          }
          // replace the original with the response and provide the new id to the updated group
          originalContentGroup = responseGroup
          updatedContentGroup.Group.groupID = responseGroup.Group.groupID
          saveContentGroup(updatedContentGroup, originalContentGroup, true)
        }
      }))
    }
    // handle requests that require a created content group here
    if (originalContentGroup.Group.groupID) {
      if (hasMetaChange) {
        const postData = {
          clientID: stateProps.clientID,
          groupID: originalContentGroup.Group.groupID,
          groupName: updatedContentGroup.Group.groupName,
          groupDescription: updatedContentGroup.Group.groupDescription
        }
        dispatch(actions.request.sendRequest({
          type: UPDATE_CONTENT_GROUP_META,
          url: `${config.uris.crud}Clientcontentgroups/updateGroupMeta`,
          postData,
        }))
      }
      if (addedCategories.length) {
        const postData = {
          clientID: stateProps.clientID,
          groupID: originalContentGroup.Group.groupID,
          categories: addedCategories.map(category => ({ categoryID: category.categoryID, inheritChildren: false }))
        }
        dispatch(actions.request.sendRequest({
          type: ADD_CONTENT_GROUP_CATEGORIES,
          url: `${config.uris.crud}Clientcontentgroups/addGroupCategories`,
          postData
        }))
      }
      if (removedCategories.length) {
        const postData = {
          clientID: stateProps.clientID,
          groupID: originalContentGroup.Group.groupID,
          categoryID: removedCategories.map(category => parseInt(category.categoryID, 10))
        }
        dispatch(actions.request.sendRequest({
          type: REMOVE_CONTENT_GROUP_CATEGORIES,
          url: `${config.uris.crud}Clientcontentgroups/removeGroupCategory`,
          postData
        }))
      }
      if (addUsers && updatedContentGroup.GroupUsers.length) {
        // add users to just created content group
        assignUsersToGroup(updatedContentGroup.GroupUsers, updatedContentGroup.Group.groupID)
      }
    }
  }

  const assignUsersToGroup = (users, groupId) => {
    // users requests must be individual
    let promises = []
    for (let i = 0; i < users.length; i++) {
      const user = users[i]
      const postData = {
        clientID: stateProps.clientID,
        groupID: groupId,
        userID: user.userId
      }
      // console.log('request ' + i + ' ' + JSON.stringify(postData))
      promises.push(dispatch(actions.request.sendRequest({
        type: ADD_CONTENT_GROUP_USER,
        url: `${config.uris.crud}Clientcontentgroups/addUser`,
        postData
      })))
    }
    Promise.all(promises)
  }

  const removeUserFromGroup = (user, groupId) => {
    // users requests must be individual
    const postData = {
      clientID: stateProps.clientID,
      groupID: groupId,
      userID: user.userId
    }
    dispatch(actions.request.sendRequest({
      type: REMOVE_CONTENT_GROUP_USER,
      url: `${config.uris.crud}Clientcontentgroups/removeUser`,
      postData
    }))
  }

  const requestUsers = () => dispatch(actions.request.sendRequest({
    type: GET_USERS,
    url: `${config.uris.crud}Users/getUsers`,
    postData: {
      clientID: stateProps.clientID
    }
  }))

  return {
    ...stateProps,
    requestCategories,
    saveContentGroup,
    assignUsersToGroup,
    removeUserFromGroup,
    requestUsers,
    ...ownProps
  }
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(GroupManager)
